import { useState } from "react";
import { Dialog } from "../CommonComponents/Dialog/Dialog";
import { ErrorToast, SuccessToast } from "../toastMessage";
import { useUser } from "../../contexts/UserContext";
/**
 * BEFORE - the token list and stamp placement came only from the built-in
 * values in config/levelThreeSigning.ts:
 * import { DSC_MESSAGES, DSC_STAMP_PLACEMENT, DSC_TOKENS, DscToken, SignableDocument, isDigitalSigningEnabled } from "../../config/levelThreeSigning";
 */
import {
  DSC_MESSAGES,
  DscToken,
  SignableDocument,
  isDigitalSigningEnabled,
} from "../../config/levelThreeSigning";
import {
  dscPlacementFrom,
  dscStampStyleFrom,
  dscTokensFrom,
  useDscStampConfig,
} from "../../utils/dscStampConfig";
import {
  checkDscToken,
  getDscErrorMessage,
  signPdfWithDsc,
} from "../../utils/dscSigner";
import { logDscSigning } from "../../utils/dscSigningLog";
import { getUnsignedDocument } from "../../utils/unsignedDocumentStore";

type ApplyDscButtonProps = {
  /** Which document is being signed; decides where the stamp is placed. */
  document: SignableDocument;
  /** The application data object used to extract dynamic stamp fields. */
  application: any;
  /** Name given to the signed file, e.g. "AID-0000832-Demand-Note-Signed.pdf". */
  signedFileName: string;
  disabled?: boolean;
  /** Receives the signed PDF, ready to be previewed and uploaded. */
  onSigned: (file: File) => void;
};

type DscStep = "closed" | "token" | "pin";

/**
 * The "Apply DSC" button for the Level 3 officer. It signs the PDF generated
 * on the previous step in two small dialogs: choose the USB token, then enter
 * its PIN. The signed PDF is handed back to the screen, which previews and
 * uploads it the same way as a manually uploaded file. Nothing is rendered
 * when digital signing is switched off.
 */
export function ApplyDscButton({
  document,
  application,
  signedFileName,
  disabled = false,
  onSigned,
}: ApplyDscButtonProps) {
  const user = useUser();
  const [step, setStep] = useState<DscStep>("closed");
  const [token, setToken] = useState<DscToken | null>(null);
  const [pin, setPin] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  /**
   * The stamp settings and token list from the server
   * (civic-plan-backend/config/dsc-stamp.json), read each time the dialogs
   * open. The built-in values are used while they load or if they fail.
   */
  const stampConfigQuery = useDscStampConfig(step !== "closed");
  const tokens = dscTokensFrom(stampConfigQuery.data);

  if (!isDigitalSigningEnabled()) return null;

  const close = () => {
    if (isBusy) return;

    setStep("closed");
    setToken(null);
    setPin("");
  };

  const onConfirmToken = async () => {
    if (!token) return;

    setIsBusy(true);
    try {
      await checkDscToken(token);
      setStep("pin");
    } catch (error) {
      ErrorToast(getDscErrorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  const onConfirmPin = async () => {
    if (!token || !pin) return;

    setIsBusy(true);
    try {
      const pdf = await getUnsignedDocument(document, application.id);

      if (!pdf) {
        ErrorToast(DSC_MESSAGES.NO_DOCUMENT);
        return;
      }

      /*
       * BEFORE - the signer program's reply was only the PDF:
       * const signedPdf = await signPdfWithDsc({ ... });
       * onSigned(new File([signedPdf], signedFileName, { type: "application/pdf" }));
       */
      const { pdf: signedPdf, signerName } = await signPdfWithDsc({
        pdf,
        token,
        pin,
        signer: { name: user?.name ?? "", email: user?.email },
        /** BEFORE: placement: DSC_STAMP_PLACEMENT[document], */
        placement: dscPlacementFrom(stampConfigQuery.data, document),
        style: dscStampStyleFrom(stampConfigQuery.data),
        application,
        document,
      });

      /**
       * The signing is written to the application's Audit History. A failure
       * here does not undo the signature, so the officer keeps the signed
       * document and is told the log entry is missing.
       */
      try {
        await logDscSigning({
          applicationId: application.id,
          document,
          signerName,
          tokenName: token.name,
        });
      } catch {
        ErrorToast(DSC_MESSAGES.LOG_NOT_SAVED);
      }

      onSigned(
        new File([signedPdf], signedFileName, { type: "application/pdf" })
      );
      SuccessToast(DSC_MESSAGES.SIGNED);
      setStep("closed");
      setToken(null);
    } catch (error) {
      ErrorToast(getDscErrorMessage(error));
    } finally {
      /** The PIN is cleared after every attempt so it never stays in memory. */
      setPin("");
      setIsBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setStep("token")}
        className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
      >
        Apply DSC
      </button>

      <Dialog
        open={step === "token"}
        onClose={close}
        className="p-6 md:min-w-[480px]"
      >
        <p className="font-bold text-xl text-grey-600 mb-4">
          Select your USB token
        </p>
        <select
          value={token?.value ?? ""}
          disabled={isBusy}
          onChange={(e) =>
            setToken(
              tokens.find((option) => option.value === e.target.value) ??
                null
            )
          }
          className="w-full border rounded-lg px-3 py-2"
        >
          <option value="" disabled>
            -- Select a token --
          </option>
          {tokens.map((option) => (
            <option key={option.value} value={option.value}>
              {option.name}
            </option>
          ))}
        </select>
        <div className="flex justify-end gap-4 mt-6">
          <button
            type="button"
            disabled={isBusy}
            onClick={close}
            className="bg-black px-6 py-2 rounded-full font-bold text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!token || isBusy}
            onClick={onConfirmToken}
            className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          >
            {isBusy ? "Checking..." : "Confirm"}
          </button>
        </div>
      </Dialog>

      <Dialog
        open={step === "pin"}
        onClose={close}
        className="p-6 md:min-w-[480px]"
      >
        <p className="font-bold text-xl text-grey-600 mb-4">
          Enter your token PIN
        </p>
        <input
          type="password"
          autoComplete="off"
          value={pin}
          disabled={isBusy}
          placeholder="PIN"
          onChange={(e) => setPin(e.target.value)}
          className="w-full border rounded-lg px-3 py-2"
        />
        <div className="flex justify-end gap-4 mt-6">
          <button
            type="button"
            disabled={isBusy}
            onClick={close}
            className="bg-black px-6 py-2 rounded-full font-bold text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!pin || isBusy}
            onClick={onConfirmPin}
            className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          >
            {isBusy ? "Signing..." : "Sign"}
          </button>
        </div>
      </Dialog>
    </>
  );
}
